import React, { useEffect, useState } from 'react'
import { getAlerts, createAlert } from '../api/alerts'
import apiClient from '../api/client'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorMessage } from '../components/ErrorMessage'
import { useAuth } from '../context/AuthContext'
import type { AlertConfig, CreateAlertRequest, AlertDirection } from '../types'
import styles from './AlertsPage.module.css'

const METRIC_OPTIONS = [
  { value: 'ctr', label: 'CTR (Click-Through Rate)' },
  { value: 'cpc', label: 'CPC (Cost per Click)' },
  { value: 'roas', label: 'ROAS (Return on Ad Spend)' },
  { value: 'spend', label: 'Spend' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'conversions', label: 'Conversions' },
]

interface DynamoCampaign {
  id: string
  product?: string
  headline?: string
  status?: string
}

// ─── Alert Card ───────────────────────────────────────────────────────────────

function AlertCard({ alert }: { alert: AlertConfig }) {
  const isAbove = alert.direction === 'above'
  return (
    <div className={styles.alertCard}>
      <div className={styles.alertHeader}>
        <div className={styles.alertMetricWrap}>
          <span className={styles.alertMetricIcon}>🔔</span>
          <span className={styles.alertMetric}>{alert.metric.toUpperCase()}</span>
        </div>
        <span className={`${styles.alertDirection} ${isAbove ? styles.above : styles.below}`}>
          {isAbove ? '▲' : '▼'} {isAbove ? 'above' : 'below'} {alert.threshold}
        </span>
      </div>
      <div className={styles.alertBody}>
        <div className={styles.alertMeta}>
          <span className={styles.alertLabel}>Campaign</span>
          <span className={styles.alertValue}>{alert.campaign_id}</span>
        </div>
        {alert.sns_topic_arn && (
          <div className={styles.alertMeta}>
            <span className={styles.alertLabel}>Notify via</span>
            <span className={styles.alertValue} title={alert.sns_topic_arn}>
              SNS: {alert.sns_topic_arn.split(':').pop()}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Create Alert Form ────────────────────────────────────────────────────────

function CreateAlertForm({ campaigns, onCreated }: {
  campaigns: DynamoCampaign[]
  onCreated: (alert: AlertConfig) => void
}) {
  const [campaignId, setCampaignId] = useState('')
  const [metric, setMetric] = useState('ctr')
  const [threshold, setThreshold] = useState('')
  const [direction, setDirection] = useState<AlertDirection>('below')
  const [snsTopicArn, setSnsTopicArn] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null); setSuccess(false)
    const thresholdNum = parseFloat(threshold)
    if (isNaN(thresholdNum)) { setError('Threshold must be a valid number.'); return }
    if (!campaignId) { setError('Please select a campaign.'); return }

    const payload: CreateAlertRequest = {
      campaign_id: campaignId,
      metric, threshold: thresholdNum, direction, sns_topic_arn: snsTopicArn,
    }
    setSubmitting(true)
    try {
      const created = await createAlert(payload)
      onCreated(created)
      setSuccess(true)
      setCampaignId(''); setMetric('ctr'); setThreshold('')
      setDirection('below'); setSnsTopicArn('')
    } catch { setError('Failed to create alert. Please try again.') }
    finally { setSubmitting(false) }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label className={styles.label}>Campaign</label>
          <select className={styles.select} value={campaignId}
            onChange={e => setCampaignId(e.target.value)} required disabled={submitting}>
            <option value="">Select a campaign…</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>
                {c.headline || c.product || `Campaign ${c.id.slice(-8)}`}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Metric to monitor</label>
          <select className={styles.select} value={metric}
            onChange={e => setMetric(e.target.value)} disabled={submitting}>
            {METRIC_OPTIONS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Alert when metric goes</label>
          <select className={styles.select} value={direction}
            onChange={e => setDirection(e.target.value as AlertDirection)} disabled={submitting}>
            <option value="below">▼ Below threshold</option>
            <option value="above">▲ Above threshold</option>
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Threshold value</label>
          <input className={styles.input} type="number" step="any"
            value={threshold} onChange={e => setThreshold(e.target.value)}
            placeholder="e.g. 0.02" required disabled={submitting} />
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.label}>SNS Topic ARN <span className={styles.optional}>(optional — for email/SMS notifications)</span></label>
          <input className={styles.input} type="text" value={snsTopicArn}
            onChange={e => setSnsTopicArn(e.target.value)}
            placeholder="arn:aws:sns:us-east-1:123456789012:my-topic"
            disabled={submitting} />
        </div>
      </div>

      {error && <div className={styles.formError} role="alert">{error}</div>}
      {success && <div className={styles.formSuccess} role="status">✓ Alert created successfully.</div>}

      <button type="submit" className={styles.submitBtn}
        disabled={submitting || !campaignId || !threshold}>
        {submitting ? 'Creating…' : '+ Create Alert'}
      </button>
    </form>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AlertsPage() {
  const { canWrite } = useAuth()
  const [alerts, setAlerts] = useState<AlertConfig[]>([])
  const [campaigns, setCampaigns] = useState<DynamoCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true); setError(null)
    try {
      // Load alerts + DynamoDB campaigns (have id, product, headline)
      const [alertsData, campsRes] = await Promise.all([
        getAlerts(),
        apiClient.get('/campaigns'),
      ])
      setAlerts(alertsData)
      setCampaigns(campsRes.data || [])
    } catch { setError('Failed to load alerts.') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  if (loading) return <LoadingSpinner message="Loading alerts…" />
  if (error) return <ErrorMessage message={error} onRetry={load} />

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.heading}>Alerts</h1>
          <p className={styles.subheading}>
            Get notified when campaign metrics cross your thresholds
          </p>
        </div>
        <div className={styles.alertCount}>
          <span className={styles.alertCountNum}>{alerts.length}</span>
          <span className={styles.alertCountLabel}>active alerts</span>
        </div>
      </div>

      {/* How it works info */}
      <div className={styles.infoBar}>
        <span>📌</span>
        <span>Alerts trigger an AWS SNS notification when a metric goes above or below your set threshold. Leave SNS ARN empty to just log the alert.</span>
      </div>

      {/* Create form — Analyst/Admin only */}
      {canWrite && (
        <div className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Create New Alert</h2>
          <CreateAlertForm campaigns={campaigns} onCreated={a => setAlerts(prev => [a, ...prev])} />
        </div>
      )}

      {/* Existing alerts */}
      <div className={styles.alertsSection}>
        <h2 className={styles.sectionTitle}>Your Alerts</h2>
        {alerts.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>🔕</span>
            <p className={styles.emptyTitle}>No alerts configured</p>
            <p className={styles.emptyText}>
              {canWrite
                ? 'Create an alert above to get notified when a metric crosses a threshold.'
                : 'No alerts have been configured for your account yet.'}
            </p>
          </div>
        ) : (
          <div className={styles.alertsGrid}>
            {alerts.map((a, i) => (
              <AlertCard key={`${a.campaign_id}-${a.metric}-${i}`} alert={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
